import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * 把输入框上的字符串属性解析成整数，解析不出有限整数时返回 null。
 * @param {string} raw
 * @returns {number | null}
 */
function toInteger(raw) {
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#syncStepperState();
  }

  /** 缺货时锁数量；恢复有货后按上下限重算加减按钮 */
  setUnavailable(unavailable) {
    const quantityInput = this.refs.quantityInput;
    if (!(quantityInput instanceof HTMLInputElement)) return;
    quantityInput.disabled = Boolean(unavailable);
    this.#syncStepperState();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.quantityInput.stepUp();
    this.#onQuantityChange('stepper');
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.quantityInput.stepDown();
    this.#onQuantityChange('stepper');
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const quantityInput = this.quantityInput;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange('input');
  }

  /**
   * Handles the quantity change event.
   * @param {'stepper' | 'input'} source - 变更来源：stepper = 加减按钮，input = 手动输入后失焦
   */
  #onQuantityChange(source) {
    const quantityInput = this.quantityInput;

    // 手动输入路径不承担「减到 0 即删除」的职责：购物车行的 input 是 min="0"，
    // 用户清空输入框（手机上 focus 全选后退格、再点空白处收键盘）会得到空串，
    // 夹到 0 就成了删除信号，整行无确认无撤销地消失。这里先把非法输入恢复成原值且不派发事件。
    if (source === 'input' && !this.#restoreInvalidQuantity(quantityInput)) {
      this.#syncStepperState();
      return;
    }

    this.#checkQuantityRules();
    this.#syncStepperState();

    const { value } = this.#readQuantityBounds(quantityInput);
    // 解析不出数值时不派发：下游按 quantity === 0 判定删除，派发 NaN 只会引起误判
    if (value == null) return;

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        value,
        Number(quantityInput.dataset.cartLine),
        quantityInput.dataset.cartLineKey || ''
      )
    );
  }

  /**
   * 手动输入的数量兜底：空串、非数字、小于 1 的值都不是有效数量，一律恢复成上一次服务端确认的数量。
   * @param {HTMLInputElement} quantityInput
   * @returns {boolean} true = 输入有效，可以继续走正常变更流程
   */
  #restoreInvalidQuantity(quantityInput) {
    const { value } = this.#readQuantityBounds(quantityInput);
    if (value != null && value >= 1) return true;

    const previous = toInteger(quantityInput.defaultValue);
    quantityInput.value = String(previous != null && previous >= 1 ? previous : 1);
    return false;
  }

  /**
   * 统一读取输入框上的数量与上下限。
   * value/min/max 在 DOM 上都是字符串，直接比较会走字典序（"9" > "10" 成立），
   * 配了 quantity_rule 的商品输入 9 会被夹成 10。#checkQuantityRules 与 #syncStepperState
   * 共用这里，避免两处解析口径漂移。
   * @param {HTMLInputElement} quantityInput
   * @returns {{ value: number | null, min: number | null, max: number | null }}
   */
  #readQuantityBounds(quantityInput) {
    return {
      value: toInteger(quantityInput.value),
      min: toInteger(quantityInput.min),
      max: toInteger(quantityInput.max),
    };
  }

  /**
   * 按 quantity_rule 把数量夹到 [min, max] 区间内。
   */
  #checkQuantityRules = () => {
    const quantityInput = this.quantityInput;
    const { value, min, max } = this.#readQuantityBounds(quantityInput);
    // 空值/非法输入交给 #restoreInvalidQuantity 处理，这里不夹取
    if (value == null) return;

    if (min != null && value < min) quantityInput.value = String(min);
    else if (max != null && value > max) quantityInput.value = String(max);
  };

  /**
   * 数量到 1（或规则下限）时禁用减号；到上限时禁用加号。
   * connectedCallback 阶段也会调用，此时 refs 可能尚未就绪，所以这里读 refs 并自行守卫，
   * 不走会抛错的 quantityInput getter。
   */
  #syncStepperState() {
    const quantityInput = this.refs.quantityInput;
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const minus = this.querySelector('.quantity-minus');
    const plus = this.querySelector('.quantity-plus');
    const { value, min, max } = this.#readQuantityBounds(quantityInput);
    const safeValue = value ?? 0;
    const floor = Math.max(min ?? 1, 1);

    if (minus instanceof HTMLButtonElement) {
      minus.disabled = quantityInput.disabled || safeValue <= floor;
    }
    if (plus instanceof HTMLButtonElement) {
      plus.disabled = quantityInput.disabled || (max != null && safeValue >= max);
    }
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
